import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Stack } from '@mui/material';
import type { CreateCustomerResponse } from '@vy/api-definition';
import { createCustomerPageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { buildEmptyRevenueBreakdown } from '@vy/services';
import { NewCustomerSchema, OVERAGE_SETTINGS, type NewCustomer } from '@vy/types';
import { isEmptyString, isNotNullOrUndefined, keys } from '@vy/utils';
import React from 'react';
import { Helmet } from 'react-helmet';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { VyPageLayout } from '../../../components/new-design/layout/VyPageLayout';
import { useAccount } from '../../../hooks/requests/use-account';
import { useCreateCustomer } from '../../../hooks/requests/use-create-customer';
import { useVyNavigate } from '../../../hooks/use-vy-navigate';
import { runMutation } from '../../../utils/vy-mutation';
import { userMessage } from '../../../utils/vy-toasts';
import { invalidateCustomersQueries } from '../../../utils/query-invalidation';
import { CreateCustomerAdvanced } from './create-customer-page-components/CreateCustomerAdvanced/CreateCustomerAdvanced';
import { CreateCustomerCompanyDetails } from './create-customer-page-components/CreateCustomerCompanyDetails/CreateCustomerCompanyDetails';
import { CreateCustomerConfiguration } from './create-customer-page-components/CreateCustomerConfiguration/CreateCustomerConfiguration';
import {
  DEFAULT_ADDRESS_COUNTRY,
  DEFAULT_DUE_DAYS,
  DEFAULT_SETTINGS_CURRENCY,
} from './create-customer-page-consts/create-customer-form-defaults';

export const CreateCustomerPage: React.FC = () => {
  const { data: accountData } = useAccount({ suspense: true });
  const paymentDueDays =
    accountData?.account?.settings.invoicingSettings?.defaultPaymentDueDays ?? DEFAULT_DUE_DAYS;
  const queryClient = useQueryClient();
  const navigate = useVyNavigate();
  const revenueBreakdown = buildEmptyRevenueBreakdown();

  const methods = useForm<NewCustomer>({
    resolver: zodResolver(NewCustomerSchema),
    defaultValues: {
      revenueBreakdown,
      settings: {
        currency: DEFAULT_SETTINGS_CURRENCY,
        paymentDueDays,
        overage: OVERAGE_SETTINGS.ChargeTier,
        taxRate: null,
      },
      address: {
        country: DEFAULT_ADDRESS_COUNTRY,
      },
      contacts: [{ email: '', name: '', title: '', phone: '', receiveInvoiceEmail: true }],
    },
  });

  const createCustomerMutation = useCreateCustomer();

  const onCustomerCreated = async ({ customer }: CreateCustomerResponse) => {
    await invalidateCustomersQueries(queryClient);
    navigate(location.pathname + `/../${customer.id}/settings`);
  };

  const onSubmit = async () => {
    const erpId = methods.getValues('erpMetadata.erpId');
    const crmId = methods.getValues('crmMetadata.crmId');
    const contacts = methods.getValues('contacts');

    if (isEmptyString(erpId)) {
      methods.setValue('erpMetadata', undefined);
    }
    if (isEmptyString(crmId)) {
      methods.setValue('crmMetadata', undefined);
    }
    const filteredContactsToCreate =
      contacts?.filter((contact) => {
        const hasAnyField =
          !isEmptyString(contact.name) ||
          !isEmptyString(contact.title) ||
          !isEmptyString(contact.phone) ||
          !isEmptyString(contact.email);
        return hasAnyField;
      }) ?? [];

    const allFields = keys<NewCustomer>(methods.getValues());

    const isValid = await methods.trigger(allFields);

    if (!isValid) {
      userMessage('error', 'Please fix the errors before saving');

      return;
    }

    const data = methods.getValues();
    const validatedNewCustomerInput = getValidNewCustomerInput({
      ...data,
      contacts: filteredContactsToCreate,
    });

    if (isNotNullOrUndefined(validatedNewCustomerInput)) {
      await runMutation(
        createCustomerMutation,
        validatedNewCustomerInput,
        {
          pending: 'Saving...',
          success: 'Saved',
          error: ({ data: { message } }) => message,
        },
        { onSuccess: onCustomerCreated },
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>Customers</title>
      </Helmet>
      <VyPageLayout
        title="Add Customer"
        sx={{
          padding: 0,
          overflow: 'hidden',
          backgroundColor: '#FFFFFF',
        }}
      >
        <FormProvider {...methods}>
          <form
            onSubmit={async (e) => {
              e.preventDefault();

              await onSubmit();
            }}
          >
            <Stack sx={{
              padding: "12px 40px"
            }}>
              <Stack spacing={3}>
                <CreateCustomerCompanyDetails />
                <CreateCustomerConfiguration />
                <CreateCustomerAdvanced />
              </Stack>
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                justifyContent: "flex-end",
                position: "sticky",
                bottom: 0,
                backgroundColor: '#FFFFFF',
                paddingLeft: 4,
                paddingRight: 4,
                paddingTop: 2,
                paddingBottom: 2,
                zIndex: 1
              }}>
              <Button
                onClick={() => methods.reset()}
                size="large"
                type="button"
                variant="outlined"
                color="primary"
              >
                Clear
              </Button>
              <Button
                {...getSelectorAttribute(createCustomerPageSelectors.saveForm)}
                size="large"
                type="submit"
                variant="contained"
                color="primary"
              >
                Save
              </Button>
            </Stack>
          </form>
        </FormProvider>
      </VyPageLayout>
    </>
  );
};

function getValidNewCustomerInput(newCustomerData: NewCustomer | null) {
  const newCustomerInputParseResult = NewCustomerSchema.safeParse(newCustomerData);

  return newCustomerInputParseResult.success ? newCustomerInputParseResult.data : undefined;
}
